import {
  ParserError,
  deserializeSchema,
  serializeSchema,
  serializeZod,
  type SerializedSchema,
  type RuntimeSchema,
  type RuntimeSchemaConfig,
  type RuntimeSchemaConfigOption,
  type RuntimeSchemaConfigSection,
  type RuntimeSchemaEntry,
  type RuntimeSchemaMap,
  type RuntimeSchemaNode,
  type ZodDescriptor,
} from '@morph-mapper/schema-serialization';
import { EntryType } from '@morph-mapper/types';
import { err, ok, type Result } from 'neverthrow';
import { z } from 'zod';
import { isRecord } from '../utils';

const INDENT = 2;

const indentLine = (level: number) => {
  return ' '.repeat(level * INDENT);
};

const stringifyKey = (key: string) => {
  return JSON.stringify(key);
};

const stringifyJsonValue = (value: unknown, level: number): string => {
  if (
    value === null ||
    typeof value === 'boolean' ||
    typeof value === 'number'
  ) {
    return JSON.stringify(value);
  }

  if (typeof value === 'string') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }

    return [
      '[',
      ...value.map(
        (item) =>
          `${indentLine(level + 1)}${stringifyJsonValue(item, level + 1)},`,
      ),
      `${indentLine(level)}]`,
    ].join('\n');
  }

  if (isRecord(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return '{}';
    }

    return [
      '{',
      ...entries.map(
        ([key, item]) =>
          `${indentLine(level + 1)}${stringifyKey(key)}: ${stringifyJsonValue(item, level + 1)},`,
      ),
      `${indentLine(level)}}`,
    ].join('\n');
  }

  return 'undefined';
};

const stringifyObject = (
  entries: Array<[string, string | undefined]>,
  level: number,
) => {
  const visibleEntries = entries.filter(
    (entry): entry is [string, string] => entry[1] !== undefined,
  );

  if (visibleEntries.length === 0) {
    return '{}';
  }

  const stringifyObjectEntry = (key: string, value: string) => {
    const propertyIndent = indentLine(level + 1);
    const valueLines = value.split('\n');

    if (valueLines.length === 1) {
      return `${propertyIndent}${stringifyKey(key)}: ${value},`;
    }

    const continuationLines = valueLines.slice(1);
    const continuationIndent = continuationLines.reduce<number | null>(
      (currentMin, line) => {
        if (line.trim().length === 0) {
          return currentMin;
        }

        const leadingWhitespace = line.match(/^\s*/)?.[0].length ?? 0;

        return currentMin === null
          ? leadingWhitespace
          : Math.min(currentMin, leadingWhitespace);
      },
      null,
    );

    const formattedLines = [
      `${propertyIndent}${stringifyKey(key)}: ${valueLines[0]}`,
      ...continuationLines.map((line) => {
        const normalizedLine =
          continuationIndent === null
            ? line
            : line.slice(Math.min(continuationIndent, line.length));

        return `${propertyIndent}${normalizedLine}`;
      }),
    ];

    const lastLineIndex = formattedLines.length - 1;
    formattedLines[lastLineIndex] = `${formattedLines[lastLineIndex]},`;

    return formattedLines.join('\n');
  };

  return [
    '{',
    ...visibleEntries.map(([key, value]) => stringifyObjectEntry(key, value)),
    `${indentLine(level)}}`,
  ].join('\n');
};

const parseStructuredJsonString = (value: string): unknown | null => {
  try {
    const parsed = JSON.parse(value);

    return Array.isArray(parsed) || isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const stringifyLiteralValue = (value: unknown): string => {
  if (typeof value !== 'string') {
    return stringifyJsonValue(value, 0);
  }

  const parsedJson = parseStructuredJsonString(value);

  if (parsedJson === null) {
    return stringifyJsonValue(value, 0);
  }

  return `JSON.stringify(${stringifyJsonValue(parsedJson, 0)})`;
};

const zodDescriptorToSource = (descriptor: ZodDescriptor): string => {
  switch (descriptor.zod) {
    case 'string':
      return 'z.string()';
    case 'number':
      return 'z.number()';
    case 'boolean':
      return 'z.boolean()';
    case 'any':
      return 'z.any()';
    case 'enum':
      return `z.enum(${stringifyJsonValue(descriptor.values, 0)})`;
    case 'literal':
      return `z.literal(${stringifyLiteralValue(descriptor.value)})`;
    case 'array':
      return `z.array(${zodDescriptorToSource(descriptor.items)})`;
    default:
      return 'z.any()';
  }
};

const stringifyZod = (
  value: RuntimeSchemaEntry['validation'],
): Result<string, Error> => {
  const serialized = serializeZod(value);

  if (serialized.isErr()) {
    return err(Error('Unsupported zod schema in runtime schema.'));
  }

  return ok(zodDescriptorToSource(serialized.value));
};

const stringifyFunction = (value: { toString(): string }) => {
  return value.toString().trim();
};

const stringifyEntryType = (value: EntryType): string => {
  const entryTypeMember = Object.entries(EntryType).find(
    ([, enumValue]) => enumValue === value,
  )?.[0];

  if (!entryTypeMember) {
    return stringifyJsonValue(value, 0);
  }

  return `EntryType.${entryTypeMember}`;
};

const stringifyEntryTypes = (values: EntryType[], level: number): string => {
  if (values.length === 0) {
    return '[]';
  }

  return [
    '[',
    ...values.map(
      (value) => `${indentLine(level + 1)}${stringifyEntryType(value)},`,
    ),
    `${indentLine(level)}]`,
  ].join('\n');
};

const stringifySchemaEntry = (
  entry: RuntimeSchemaEntry,
  level: number,
): Result<string, Error> => {
  const validation = stringifyZod(entry.validation);

  if (validation.isErr()) {
    return err(validation.error);
  }

  return ok(
    stringifyObject(
      [
        [
          'displayName',
          entry.displayName
            ? stringifyJsonValue(entry.displayName, level + 1)
            : undefined,
        ],
        ['validation', validation.value],
        ['type', stringifyEntryType(entry.type)],
        [
          'allowedTypes',
          entry.allowedTypes
            ? stringifyEntryTypes(entry.allowedTypes, level + 1)
            : undefined,
        ],
        [
          'prefill',
          entry.prefill ? stringifyFunction(entry.prefill) : undefined,
        ],
        [
          'configKey',
          entry.configKey
            ? stringifyJsonValue(entry.configKey, level + 1)
            : undefined,
        ],
        [
          'semantics',
          entry.semantics
            ? stringifyJsonValue(entry.semantics, level + 1)
            : undefined,
        ],
        [
          'updateObject',
          entry.updateObject
            ? stringifyJsonValue(entry.updateObject, level + 1)
            : undefined,
        ],
      ],
      level,
    ),
  );
};

const stringifySchemaNode = (
  node: RuntimeSchemaNode,
  level: number,
): Result<string, Error> => {
  if ('validation' in node) {
    return stringifySchemaEntry(node, level);
  }

  return stringifySchemaMap(node, level);
};

const stringifySchemaMap = (
  map: RuntimeSchemaMap,
  level: number,
): Result<string, Error> => {
  const valuesEntries: Array<[string, string]> = [];

  for (const [key, node] of Object.entries(map.values)) {
    const stringifiedNode = stringifySchemaNode(node, level + 2);

    if (stringifiedNode.isErr()) {
      return err(stringifiedNode.error);
    }

    valuesEntries.push([key, stringifiedNode.value]);
  }

  const values = stringifyObject(valuesEntries, level + 1);

  return ok(
    stringifyObject(
      [
        [
          'displayName',
          map.displayName
            ? stringifyJsonValue(map.displayName, level + 1)
            : undefined,
        ],
        [
          'outputType',
          map.outputType
            ? stringifyJsonValue(map.outputType, level + 1)
            : undefined,
        ],
        [
          'configKey',
          map.configKey
            ? stringifyJsonValue(map.configKey, level + 1)
            : undefined,
        ],
        ['values', values],
      ],
      level,
    ),
  );
};

const stringifyConfigOption = (
  option: RuntimeSchemaConfigOption,
  level: number,
): Result<string, Error> => {
  const type = stringifyZod(option.type);

  if (type.isErr()) {
    return err(type.error);
  }

  return ok(
    stringifyObject(
      [
        ['type', type.value],
        ['title', stringifyJsonValue(option.title, level + 1)],
        ['description', stringifyJsonValue(option.description, level + 1)],
        ['input', stringifyJsonValue(option.input, level + 1)],
        [
          'selectionDeclareVariable',
          option.selectionDeclareVariable === undefined
            ? undefined
            : stringifyJsonValue(option.selectionDeclareVariable, level + 1),
        ],
        [
          'selection',
          option.selection
            ? stringifyJsonValue(option.selection, level + 1)
            : undefined,
        ],
        [
          'default',
          option.default === undefined
            ? undefined
            : stringifyJsonValue(option.default, level + 1),
        ],
        [
          'placeholder',
          option.placeholder
            ? stringifyJsonValue(option.placeholder, level + 1)
            : undefined,
        ],
        [
          'advanced',
          option.advanced === undefined
            ? undefined
            : stringifyJsonValue(option.advanced, level + 1),
        ],
        [
          'options',
          option.options
            ? stringifyJsonValue(option.options, level + 1)
            : undefined,
        ],
        [
          'conditions',
          option.conditions
            ? stringifyJsonValue(option.conditions, level + 1)
            : undefined,
        ],
      ],
      level,
    ),
  );
};

const stringifyConfigSection = (
  section: RuntimeSchemaConfigSection,
  level: number,
): Result<string, Error> => {
  const optionEntries: Array<[string, string]> = [];

  for (const [key, option] of Object.entries(section.options)) {
    const stringifiedOption = stringifyConfigOption(option, level + 2);

    if (stringifiedOption.isErr()) {
      return err(stringifiedOption.error);
    }

    optionEntries.push([key, stringifiedOption.value]);
  }

  const options = stringifyObject(optionEntries, level + 1);

  return ok(
    stringifyObject(
      [
        ['title', stringifyJsonValue(section.title, level + 1)],
        [
          'description',
          section.description
            ? stringifyJsonValue(section.description, level + 1)
            : undefined,
        ],
        [
          'hidden',
          section.hidden === undefined
            ? undefined
            : stringifyJsonValue(section.hidden, level + 1),
        ],
        [
          'advanced',
          section.advanced === undefined
            ? undefined
            : stringifyJsonValue(section.advanced, level + 1),
        ],
        [
          'childConfigurations',
          section.childConfigurations
            ? stringifyJsonValue(section.childConfigurations, level + 1)
            : undefined,
        ],
        ['options', options],
        [
          'conditions',
          section.conditions
            ? stringifyJsonValue(section.conditions, level + 1)
            : undefined,
        ],
        ['mutate', stringifyFunction(section.mutate)],
      ],
      level,
    ),
  );
};

const stringifyConfig = (
  config: RuntimeSchemaConfig | undefined,
  level: number,
): Result<string, Error> => {
  if (!config || Object.keys(config).length === 0) {
    return ok('{}');
  }

  const sectionEntries: Array<[string, string]> = [];

  for (const [key, section] of Object.entries(config)) {
    const stringifiedSection = stringifyConfigSection(section, level + 1);

    if (stringifiedSection.isErr()) {
      return err(stringifiedSection.error);
    }

    sectionEntries.push([key, stringifiedSection.value]);
  }

  return ok(stringifyObject(sectionEntries, level));
};

const toRuntimeSchemaErrorMessage = (error: ParserError) => {
  switch (error) {
    case ParserError.ZodDeserializationError:
      return 'Failed to deserialize the stored zod schema.';
    case ParserError.FunctionDeserializationError:
      return 'Failed to deserialize a stored schema function.';
    case ParserError.ZodSerializationError:
      return 'Failed to serialize the runtime schema.';
    default:
      return 'Failed to process the schema.';
  }
};

const stringifyRuntimeSchema = (
  schema: RuntimeSchema,
): Result<string, Error> => {
  const config = stringifyConfig(schema.config, 1);

  if (config.isErr()) {
    return err(config.error);
  }

  const definition = stringifySchemaMap(schema.definition, 1);

  if (definition.isErr()) {
    return err(definition.error);
  }

  const runtimeObjectSource = stringifyObject(
    [
      [
        'updateTemplateDiscardFields',
        stringifyJsonValue(schema.updateTemplateDiscardFields, 1),
      ],
      ['config', config.value],
      ['definition', definition.value],
    ],
    0,
  );

  return ok(['(', runtimeObjectSource, ')'].join('\n'));
};

export const deserializeSchemaToEditorSource = (
  schema: unknown,
): Result<string, string> => {
  const runtimeSchema = deserializeSchema(schema);

  if (runtimeSchema.isErr()) {
    return err(toRuntimeSchemaErrorMessage(runtimeSchema.error));
  }

  const source = stringifyRuntimeSchema(runtimeSchema.value);

  if (source.isErr()) {
    return err(source.error.message);
  }

  return ok(source.value);
};

export const serializeEditorSourceToSchema = (
  source: string,
): Result<SerializedSchema, string> => {
  let runtimeSchema: unknown;

  try {
    runtimeSchema = Function(
      'EntryType',
      'z',
      'defineRuntimeSchema',
      `"use strict"; return defineRuntimeSchema(${source.trim() || '{}'});`,
    )(EntryType, z, (value: RuntimeSchema) => value);
  } catch (error) {
    return err(
      error instanceof Error
        ? `Schema source is not valid JavaScript: ${error.message}`
        : 'Schema source is not valid JavaScript.',
    );
  }

  try {
    return serializeSchema(runtimeSchema as RuntimeSchema).mapErr(
      toRuntimeSchemaErrorMessage,
    );
  } catch (error) {
    return err(
      error instanceof Error
        ? `Schema source does not match the expected runtime schema shape: ${error.message}`
        : 'Schema source does not match the expected runtime schema shape.',
    );
  }
};
